import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ActivePanelEnum } from '@app/core/enums/active-panel.enum';
import { ActivePanelInterface, PendingActionInterface } from '@app/core/interfaces';
import { Dossier } from '@app/core/interfaces/dossier';
import { ScrollService } from '@app/core/scroll/scroll.service';
import { PanelService } from '@app/shared/services/panel.service';
import { PanelExpandableComponent } from '@app/shared/ux/panel-expandable/panel-expandable.component';
import { DateService } from 'outshared-lib';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';

@Component({
    selector: 'ins-damage-claim',
    templateUrl: './damage-claim.component.html',
    styleUrls: ['./damage-claim.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DamageClaimComponent implements OnChanges, AfterViewInit, OnDestroy {
    @ViewChildren(PanelExpandableComponent) children: QueryList<PanelExpandableComponent>;

    @Input() claim: Dossier;
    @Input() actions: PendingActionInterface;

    public activePanels: { [id: string]: boolean };
    public showDas: boolean;
    public showSar: boolean;
    public activePanelEnum: typeof ActivePanelEnum;

    private defaultPanelId: ActivePanelEnum;
    private defaultPanelDate$: Subject<string>;
    private subscription: Subscription;

    constructor(
        private scrollService: ScrollService,
        private route: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        private router: Router,
        private dateService: DateService,
        private panelService: PanelService,
    ) {
        this.activePanels = {
            [ActivePanelEnum.AppointmentPanel]: false,
            [ActivePanelEnum.ClaimNotificationPanel]: false,
            [ActivePanelEnum.InboxPanel]: false,
            [ActivePanelEnum.InvolvedPartyPanel]: false,
            [ActivePanelEnum.PaperworkPanel]: false,
            [ActivePanelEnum.PendingActionsPanel]: false,
            [ActivePanelEnum.PhotoPanel]: false,
        };
        this.showDas = false;
        this.showSar = false;
        this.activePanelEnum = ActivePanelEnum;
        this.defaultPanelId = ActivePanelEnum.ClaimNotificationPanel;
        this.defaultPanelDate$ = new Subject();
        this.subscription = new Subscription();
    }

    ngAfterViewInit(): void {
        const queryParams = this.route.queryParams
            .pipe(
                delay(0),
                filter((params) => !!Object.values(params).length),
            )
            .subscribe((params) => this.togglePanel(params));

        const defaultPanel = combineLatest([this.route.queryParams, this.defaultPanelDate$])
            .pipe(
                delay(0),
                filter(([params, date]) => params.hasOwnProperty('section')),
                map(([params, date]) => date),
                take(1),
            )
            .subscribe((date) => this.expandDefaultPanel(date));

        this.subscription.add(queryParams);
        this.subscription.add(defaultPanel);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.claim && changes.claim.currentValue) {
            this.isDasClaim(changes.claim.currentValue);
            this.isSarClaim(changes.claim.currentValue);
            this.setDefaultPanelDate(changes.claim.currentValue);
        }
    }

    public open(section: ActivePanelEnum): void {
        this.router.navigate([], {
            queryParams: {
                section,
                expand: true,
                scroll: true,
            },
            queryParamsHandling: 'merge',
        });
    }

    public close(): void {
        this.closeAllPanels();
        this.clearActivePanels();
        this.clearQueryParams();
    }

    private togglePanel(props: Params): void {
        const { section } = props;
        const panel = this.children.find((child) => child.id === section);

        if (!panel) {
            return;
        }

        this.closeAllPanels();
        this.clearActivePanels();
        this.panelService.handleQueryParams(
            { panel: panel.id, scrollTo: panel.id } as ActivePanelInterface,
            this.children,
            this.changeDetectorRef,
        );
        this.setActivePanel(section);
    }

    private clearQueryParams(): void {
        this.router.navigate([], {
            queryParams: {
                section: '',
                expand: false,
                scroll: false,
            },
            queryParamsHandling: 'merge',
        });
    }

    private closeAllPanels(): void {
        if (!this.children) {
            return;
        }

        this.children.forEach((child) => child.close());
    }

    private clearActivePanels(): void {
        const panels = Object.keys(this.activePanels);
        panels.forEach((panel) => (this.activePanels[panel] = false));
    }

    private setActivePanel(section: ActivePanelEnum): void {
        if (!section) {
            return;
        }

        this.activePanels[section] = true;
    }

    private expandDefaultPanel(date: string): void {
        const defaultPanel = this.children.find((child) => child.id === this.defaultPanelId);
        const beforeDate = this.dateService.date(date).add(2, 'month');
        const isValidDate = this.dateService.isBefore(date, beforeDate);

        if (defaultPanel && isValidDate) {
            defaultPanel.open();
            this.setActivePanel(this.defaultPanelId);
        }
    }

    private isDasClaim(dossier: Dossier): void {
        if (!dossier.claim || dossier.claim.length === 0) {
            return;
        }

        if (!dossier.claim[0].behandelaar) {
            return;
        }

        if (dossier.claim[0].behandelaar.adres_relatie_id === 1000009) {
            this.showDas = true;
        }
    }

    private isSarClaim(dossier: Dossier): void {
        if (!dossier.claim || dossier.claim.length === 0) {
            return;
        }

        if (!dossier.claim[0].behandelaar) {
            return;
        }

        if (dossier.claim[0].behandelaar.adres_relatie_id === 1062755) {
            this.showSar = true;
        }
    }

    private setDefaultPanelDate(claim: Dossier): void {
        const date = claim.schadedatum;

        if (!date) {
            return;
        }

        this.defaultPanelDate$.next(date);
    }
}
